/* Killerani redesign 2026: hero card fan, holo tilt, 3D card ring, scroll depth, drop countdown.
   Everything degrades: no JS shows the stacked fan and a static ring, reduced motion shows a flat row.
   Every component registers its listeners on an AbortController so the theme editor can unload it cleanly. */
(() => {
  'use strict';

  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const mod = (n, m) => ((n % m) + m) % m;

  /* ---------- lifecycle: one controller per component ---------- */
  const setup = (el) => {
    if (el.dataset.kaReady) return null;
    el.dataset.kaReady = '1';
    const ctrl = new AbortController();
    const cleanups = [];
    el._kaAbort = () => {
      ctrl.abort();
      cleanups.forEach((fn) => fn());
      delete el.dataset.kaReady;
    };
    return { signal: ctrl.signal, onCleanup: (fn) => cleanups.push(fn) };
  };

  const watch = (el, life, cb) => {
    if (!('IntersectionObserver' in window)) { cb(true); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => cb(e.isIntersecting)), { rootMargin: '120px 0px' });
    io.observe(el);
    life.onCleanup(() => io.disconnect());
  };

  /* ---------- hero fan ---------- */
  function initFan(fan) {
    const life = setup(fan);
    if (!life) return;
    const { signal } = life;
    const stage = fan.querySelector('.ka-fan__stage');
    const cards = [...fan.querySelectorAll('.ka-card')];

    const deal = () => fan.classList.add('is-dealt');
    if (RM.matches) { deal(); return; }
    const t = setTimeout(deal, 180);
    life.onCleanup(() => clearTimeout(t));

    let tx = 0, ty = 0, cx = 0, cy = 0, hx = 50, hy = 35, raf = 0, visible = true;
    const tick = () => {
      cx = lerp(cx, tx, 0.08);
      cy = lerp(cy, ty, 0.08);
      stage.style.setProperty('--sx', cy.toFixed(2) + 'deg');
      stage.style.setProperty('--sy', cx.toFixed(2) + 'deg');
      cards.forEach((c, i) => {
        c.style.setProperty('--mx', (hx + (i - 1) * 18).toFixed(1) + '%');
        c.style.setProperty('--my', hy.toFixed(1) + '%');
      });
      raf = (Math.abs(cx - tx) > 0.02 || Math.abs(cy - ty) > 0.02) && visible ? requestAnimationFrame(tick) : 0;
    };
    const kick = () => { if (!raf && visible) raf = requestAnimationFrame(tick); };
    life.onCleanup(() => cancelAnimationFrame(raf));

    window.addEventListener('pointermove', (e) => {
      if (!FINE.matches) return;
      const px = e.clientX / window.innerWidth;
      const py = e.clientY / window.innerHeight;
      tx = (px - 0.5) * 22;
      ty = -(py - 0.5) * 14;
      hx = px * 100;
      hy = py * 100;
      kick();
    }, { passive: true, signal });
    const onScroll = () => {
      if (FINE.matches) return;
      const r = fan.getBoundingClientRect();
      const p = clamp(1 - (r.top + r.height / 2) / window.innerHeight, -0.5, 1);
      ty = 8 - p * 16;
      tx = Math.sin(p * Math.PI) * 10;
      hx = 30 + p * 50;
      hy = 20 + p * 40;
      kick();
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    watch(fan, life, (v) => { visible = v; if (v) kick(); });
    onScroll();
  }

  /* ---------- tilt with glare (product cards) ---------- */
  function initTilt(el) {
    const life = setup(el);
    if (!life) return;
    const { signal } = life;
    const max = parseFloat(el.dataset.kaTilt) || 8;
    const target = el.querySelector('.ka-prod__media') || el;
    let tx = 0, ty = 0, cx = 0, cy = 0, raf = 0;
    const tick = () => {
      cx = lerp(cx, tx, 0.14);
      cy = lerp(cy, ty, 0.14);
      target.style.setProperty('--rx', cy.toFixed(2) + 'deg');
      target.style.setProperty('--ry', cx.toFixed(2) + 'deg');
      raf = (Math.abs(cx - tx) > 0.03 || Math.abs(cy - ty) > 0.03) ? requestAnimationFrame(tick) : 0;
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(tick); };
    life.onCleanup(() => cancelAnimationFrame(raf));
    el.addEventListener('pointermove', (e) => {
      if (!FINE.matches || RM.matches) return;
      const r = target.getBoundingClientRect();
      const px = clamp((e.clientX - r.left) / r.width, 0, 1);
      const py = clamp((e.clientY - r.top) / r.height, 0, 1);
      tx = (px - 0.5) * 2 * max;
      ty = -(py - 0.5) * 2 * max;
      target.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
      target.style.setProperty('--my', (py * 100).toFixed(1) + '%');
      el.classList.add('is-tilting');
      kick();
    }, { signal });
    el.addEventListener('pointerleave', () => {
      tx = 0; ty = 0;
      el.classList.remove('is-tilting');
      kick();
    }, { signal });
  }

  /* ---------- scroll depth: sets --p from 0 (entering) to 1 (settled) ---------- */
  const scrollEls = new Set();
  let scrollRaf = 0;
  const updateScroll = () => {
    scrollRaf = 0;
    const vh = window.innerHeight;
    scrollEls.forEach((el) => {
      if (!el.isConnected) { scrollEls.delete(el); return; }
      const r = el.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      const p = clamp((vh - r.top) / (vh * 0.75), 0, 1);
      el.style.setProperty('--p', (1 - Math.pow(1 - p, 3)).toFixed(3));
    });
  };
  const queueScroll = () => { if (!scrollRaf) scrollRaf = requestAnimationFrame(updateScroll); };
  function initScroll(el) {
    const life = setup(el);
    if (!life) return;
    if (RM.matches) { el.style.setProperty('--p', '1'); return; }
    scrollEls.add(el);
    life.onCleanup(() => scrollEls.delete(el));
    queueScroll();
  }
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll);

  /* ---------- countdown: days only, counted in Berlin time, the launch hour is not fixed ---------- */
  const berlinToday = () => {
    const s = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const [y, mo, d] = s.split('-').map(Number);
    return Date.UTC(y, mo - 1, d);
  };
  function initCountdown(el) {
    const life = setup(el);
    if (!life) return;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(el.dataset.date || '');
    if (!m) return;
    const num = el.querySelector('[data-ka-count-num]');
    const label = el.querySelector('[data-ka-count-label]');
    const section = el.closest('.ka-section');
    const liveLink = section && section.querySelector('[data-ka-live-link]');
    const baseLabel = label ? label.textContent : '';
    const launch = Date.UTC(+m[1], +m[2] - 1, +m[3]);
    const render = () => {
      const days = Math.round((launch - berlinToday()) / 86400000);
      if (days > 0) {
        el.classList.add('is-counting');
        num.textContent = String(days);
        if (label) label.textContent = days === 1 ? baseLabel.replace(/days/i, 'day') : baseLabel;
      } else {
        // data-live-midnight: the drop opens at 00:00 Berlin time, so launch day already counts as live
        const liveNow = days < 0 || (days === 0 && el.dataset.liveMidnight === 'true');
        el.classList.remove('is-counting');
        num.textContent = liveNow ? (el.dataset.liveLabel || 'Out now') : 'Today';
        if (liveLink && liveNow) liveLink.hidden = false;
        if (liveNow && section) section.querySelectorAll('[data-ka-prelaunch]').forEach((n) => { n.hidden = true; });
      }
    };
    render();
    const iv = setInterval(render, 60 * 60 * 1000);
    life.onCleanup(() => clearInterval(iv));
  }

  /* ---------- 3D card ring ---------- */
  function initRing(section) {
    const life = setup(section);
    if (!life) return;
    const { signal } = life;
    const stage = section.querySelector('[data-ka-ringstage]');
    const ring = section.querySelector('[data-ka-ring]');
    const items = [...ring.children];
    const n = items.length;
    if (!n) return;
    const cards = items.map((it) => it.querySelector('[data-ka-card]'));
    const status = section.querySelector('[data-ka-status]');
    const names = cards.map((c) => (c.querySelector('.ka-card__name') || {}).textContent || '');
    const step = 360 / n;

    const flipOnly = (btn) => {
      const on = btn.getAttribute('aria-pressed') !== 'true';
      cards.forEach((c) => c !== btn && c.setAttribute('aria-pressed', 'false'));
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    };

    if (RM.matches) {
      cards.forEach((btn) => btn.addEventListener('click', () => flipOnly(btn), { signal }));
      return;
    }

    let angle = 0, vel = 0, target = null, dragging = false, moved = 0, lastX = 0, lastT = 0;
    let visible = false, raf = 0, hover = false, idleUntil = 0, front = -1, cur = 0, announce = false;

    const layout = () => {
      const w = items[0].offsetWidth || 200;
      const r = Math.round((w / 2) / Math.tan(Math.PI / n) + w * 0.22);
      section.style.setProperty('--ka-r', r + 'px');
      items.forEach((it, i) => it.style.setProperty('--a', (i * step) + 'deg'));
    };

    const frontIndex = () => mod(Math.round(-angle / step), n);
    const goTo = (i, speak = true) => {
      cur = mod(i, n);
      const base = -cur * step;
      const k = Math.round((angle - base) / 360);
      target = base + k * 360;
      vel = 0;
      announce = speak;
      idleUntil = performance.now() + 6000;
      kick();
    };

    const paint = () => {
      ring.style.setProperty('--angle', angle.toFixed(3) + 'deg');
      items.forEach((it, i) => {
        const f = Math.cos(((i * step + angle) * Math.PI) / 180);
        it.style.setProperty('--f', clamp(f, 0, 1).toFixed(3));
      });
      const fi = frontIndex();
      if (fi !== front) {
        if (front >= 0) {
          items[front].classList.remove('is-front');
          cards[front].setAttribute('aria-pressed', 'false');
          cards[front].setAttribute('tabindex', '-1');
        }
        front = fi;
        if (target === null) cur = fi;
        items[front].classList.add('is-front');
        cards[front].setAttribute('tabindex', '0');
      }
      if (announce && target === null && status) {
        status.textContent = names[front];
        announce = false;
      }
    };

    const tick = (t) => {
      raf = 0;
      if (!dragging) {
        if (target !== null) {
          angle = lerp(angle, target, 0.1);
          if (Math.abs(angle - target) < 0.04) { angle = target; target = null; }
        } else if (Math.abs(vel) > 0.01) {
          angle += vel;
          vel *= 0.94;
          if (Math.abs(vel) <= 0.01) { vel = 0; goTo(frontIndex(), false); }
        } else if (!hover && t > idleUntil && !section.contains(document.activeElement)) {
          angle -= 0.045;
        }
      }
      paint();
      if (visible) raf = requestAnimationFrame(tick);
    };
    const kick = () => { if (!raf && visible) raf = requestAnimationFrame(tick); };
    life.onCleanup(() => cancelAnimationFrame(raf));

    stage.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      dragging = true; moved = 0; lastX = e.clientX; lastT = e.timeStamp; vel = 0; target = null;
      stage.classList.add('is-dragging');
    }, { signal });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dt = Math.max(1, e.timeStamp - lastT);
      lastX = e.clientX; lastT = e.timeStamp;
      moved += Math.abs(dx);
      const d = dx * (240 / Math.max(320, stage.clientWidth));
      angle += d;
      vel = clamp(d * (16 / dt), -8, 8);
      idleUntil = performance.now() + 6000;
      kick();
    }, { passive: true, signal });
    const end = () => {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      if (moved <= 6) vel = 0;
      if (Math.abs(vel) < 0.2) goTo(frontIndex(), false);
      kick();
    };
    window.addEventListener('pointerup', end, { signal });
    window.addEventListener('pointercancel', end, { signal });

    cards.forEach((btn, i) => {
      btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
      btn.addEventListener('click', (e) => {
        const wasDrag = e.detail !== 0 && moved > 6;
        moved = 0;
        if (wasDrag) { e.preventDefault(); return; }
        vel = 0;
        if (i !== frontIndex() || target !== null) { goTo(i); return; }
        flipOnly(btn);
        idleUntil = performance.now() + 8000;
      }, { signal });
      btn.addEventListener('focus', () => {
        if (!dragging && btn.matches(':focus-visible') && i !== frontIndex()) goTo(i);
      }, { signal });
    });

    const prev = section.querySelector('[data-ka-prev]');
    const next = section.querySelector('[data-ka-next]');
    if (prev) prev.addEventListener('click', () => goTo(cur - 1), { signal });
    if (next) next.addEventListener('click', () => goTo(cur + 1), { signal });
    section.addEventListener('keydown', (e) => {
      if (!e.target.closest('[data-ka-card]')) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(cur + (e.key === 'ArrowRight' ? 1 : -1));
        cards[cur].focus({ preventScroll: true });
      }
    }, { signal });
    stage.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = true; }, { signal });
    stage.addEventListener('pointerleave', () => { hover = false; }, { signal });

    layout();
    paint();
    if (status) status.textContent = names[0];
    window.addEventListener('resize', () => { layout(); paint(); }, { signal });
    watch(section, life, (v) => { visible = v; if (v) kick(); });
  }

  /* ---------- boot, also for the theme editor ---------- */
  const boot = (root = document) => {
    root.querySelectorAll('[data-ka-fan]').forEach(initFan);
    root.querySelectorAll('[data-ka-tilt]').forEach(initTilt);
    root.querySelectorAll('[data-ka-scroll]').forEach(initScroll);
    root.querySelectorAll('[data-ka-countdown]').forEach(initCountdown);
    root.querySelectorAll('[data-ka-killers]').forEach(initRing);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-ka-ready]').forEach((el) => el._kaAbort && el._kaAbort());
  });
})();
